import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, PaperAirplaneIcon, UserIcon, MapPinIcon,
  CubeIcon, TruckIcon, ClipboardDocumentCheckIcon,
  BoltIcon, ClockIcon, CheckCircleIcon, DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const CreateShipment = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const senderRef = useRef(null);
  const recipientRef = useRef(null);
  const packageRef = useRef(null);

  const { register, handleSubmit, formState: { errors }, getValues, setValue, watch } = useForm({
    defaultValues: {
      serviceType: 'standard',
      signatureRequired: false,
      fragile: false
    }
  });

  const watchedServiceType = watch('serviceType');

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copySenderToRecipient = () => {
    const senderFields = ['senderName', 'senderAddress', 'senderCity', 'senderState', 'senderPostalCode', 'senderCountry'];
    const recipientMap = {
      senderName: 'recipientName',
      senderAddress: 'recipientAddress',
      senderCity: 'recipientCity',
      senderState: 'recipientState',
      senderPostalCode: 'recipientPostalCode',
      senderCountry: 'recipientCountry'
    };
    const values = getValues();
    senderFields.forEach(field => {
      if (values[field]) {
        setValue(recipientMap[field], values[field]);
      }
    });
    toast.success('Sender address copied to recipient');
  };

  const calculateEstimatedDelivery = (serviceType) => {
    const days = { economy: 7, standard: 5, express: 2, overnight: 1 };
    const date = new Date();
    date.setDate(date.getDate() + (days[serviceType] || 5));
    return date.toISOString().split('T')[0];
  };

  const handleServiceTypeChange = (value) => {
    setValue('serviceType', value);
    const estimated = calculateEstimatedDelivery(value);
    setValue('estimatedDelivery', estimated);
  };

  const createShipmentMutation = useMutation({
    mutationFn: adminAPI.createShipment,
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Shipment created successfully!');
        navigate(`/admin/shipments/${data.data.id}`);
      } else {
        toast.error(data.message || 'Failed to create shipment');
      }
      setIsSubmitting(false);
    },
    onError: (error) => {
      let message;
      if (!error.response) {
        message = 'Network error. Check that the backend server is running and try again.';
      } else if (error.response.data && error.response.data.message) {
        message = error.response.data.message;
      } else if (error.response.status === 401) {
        message = 'Session expired. Please log in again.';
      } else if (error.response.status === 429) {
        message = 'Too many requests. Please wait a moment and try again.';
      } else {
        message = 'Failed to create shipment';
      }
      toast.error(message);
      console.error('Create shipment error:', error);
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data) => {
    setIsSubmitting(true);
    // Trim strings and remove empty optional fields so backend validation doesn't reject empty strings
    const cleaned = Object.fromEntries(
      Object.entries(data)
        .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    createShipmentMutation.mutate(cleaned);
  };

  const serviceTypes = [
    { value: 'economy', label: 'Economy', days: '5-7 days', price: '$6.99', icon: ClockIcon, color: 'gray' },
    { value: 'standard', label: 'Standard', days: '3-5 days', price: '$9.99', icon: TruckIcon, color: 'blue' },
    { value: 'express', label: 'Express', days: '1-2 days', price: '$19.99', icon: BoltIcon, color: 'orange' },
    { value: 'overnight', label: 'Overnight', days: 'Next day', price: '$29.99', icon: BoltIcon, color: 'red' }
  ];

  const countries = [
    'United States', 'Canada', 'Mexico', 'United Kingdom', 'Germany', 'France',
    'Netherlands', 'Spain', 'Italy', 'Australia', 'Japan', 'China', 'India',
    'Brazil', 'Argentina', 'South Africa', 'United Arab Emirates', 'Saudi Arabia',
    'Nigeria', 'Kenya', 'Egypt', 'Nepal', 'Singapore', 'South Korea', 'Other'
  ];

  const steps = [
    { label: 'Sender', icon: UserIcon, ref: senderRef },
    { label: 'Recipient', icon: MapPinIcon, ref: recipientRef },
    { label: 'Package', icon: CubeIcon, ref: packageRef }
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA] pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/shipments')}
            className="flex items-center text-[#64748B] hover:text-[#0B1F3A] mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Shipments
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B1F3A]">Create New Shipment</h1>
          <p className="text-[#64748B] mt-1 text-sm md:text-base">
            Fill in the details below to create a new shipment tracking record
          </p>
        </div>

        {/* Step Progress Indicator */}
        <div className="flex items-center justify-between mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            const isActive = activeStep === idx;
            const isComplete = activeStep > idx;
            return (
              <React.Fragment key={step.label}>
                <button
                  type="button"
                  onClick={() => { setActiveStep(idx); scrollToSection(step.ref); }}
                  className="flex flex-col items-center gap-1.5 flex-shrink-0"
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                    isComplete ? 'bg-green-500 text-white' :
                    isActive ? 'bg-[#FF6B00] text-white ring-4 ring-orange-100' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {isComplete ? <CheckCircleIcon className="h-6 w-6" /> : <StepIcon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs font-medium ${isActive ? 'text-[#0B1F3A]' : 'text-gray-400'}`}>
                    {step.label}
                  </span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 transition-colors ${activeStep > idx ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Sender Information */}
          <div className="card" ref={senderRef}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-[#0B1F3A] flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-[#FF6B00]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0B1F3A]">Sender Information</h2>
                <p className="text-xs text-[#64748B]">Where the package is shipping from</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="senderName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="senderName"
                  type="text"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderName', { required: 'Sender name is required' })}
                  className={`input ${errors.senderName ? 'border-red-500' : ''}`}
                  placeholder="e.g. Daniel Hartman"
                />
                {errors.senderName && (
                  <p className="mt-1 text-sm text-red-600">{errors.senderName.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="senderEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <input
                  id="senderEmail"
                  type="email"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderEmail')}
                  className="input"
                  placeholder="daniel.hartman@email.com"
                />
              </div>

              <div>
                <label htmlFor="senderPhone" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  id="senderPhone"
                  type="tel"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderPhone')}
                  className="input"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="senderAddress" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="senderAddress"
                  type="text"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderAddress', { required: 'Sender address is required' })}
                  className={`input ${errors.senderAddress ? 'border-red-500' : ''}`}
                  placeholder="123 Main Street"
                />
                {errors.senderAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.senderAddress.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="senderCity" className="block text-sm font-medium text-gray-700 mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="senderCity"
                  type="text"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderCity', { required: 'Sender city is required' })}
                  className={`input ${errors.senderCity ? 'border-red-500' : ''}`}
                  placeholder="New York"
                />
                {errors.senderCity && (
                  <p className="mt-1 text-sm text-red-600">{errors.senderCity.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="senderState" className="block text-sm font-medium text-gray-700 mb-1.5">
                  State/Province
                </label>
                <input
                  id="senderState"
                  type="text"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderState')}
                  className="input"
                  placeholder="NY"
                />
              </div>

              <div>
                <label htmlFor="senderPostalCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="senderPostalCode"
                  type="text"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderPostalCode', { required: 'Postal code is required' })}
                  className={`input ${errors.senderPostalCode ? 'border-red-500' : ''}`}
                  placeholder="10001"
                />
                {errors.senderPostalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.senderPostalCode.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="senderCountry" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  id="senderCountry"
                  onFocus={() => setActiveStep(0)}
                  {...register('senderCountry', { required: 'Country is required' })}
                  className={`input ${errors.senderCountry ? 'border-red-500' : ''}`}
                >
                  <option value="">Select country...</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.senderCountry && (
                  <p className="mt-1 text-sm text-red-600">{errors.senderCountry.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="card" ref={recipientRef}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#0B1F3A] flex items-center justify-center">
                  <MapPinIcon className="h-6 w-6 text-[#FF6B00]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#0B1F3A]">Recipient Information</h2>
                  <p className="text-xs text-[#64748B]">Where the package is going</p>
                </div>
              </div>
              <button
                type="button"
                onClick={copySenderToRecipient}
                className="flex items-center gap-1.5 text-xs font-medium text-[#FF6B00] hover:text-[#e65f00] bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Same as sender
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="recipientName"
                  type="text"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientName', { required: 'Recipient name is required' })}
                  className={`input ${errors.recipientName ? 'border-red-500' : ''}`}
                  placeholder="e.g. Olivia Bennett"
                />
                {errors.recipientName && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientName.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <input
                  id="recipientEmail"
                  type="email"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientEmail')}
                  className="input"
                  placeholder="olivia.bennett@email.com"
                />
              </div>

              <div>
                <label htmlFor="recipientPhone" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  id="recipientPhone"
                  type="tel"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientPhone')}
                  className="input"
                  placeholder="+1 (555) 987-6543"
                />
              </div>

              <div>
                <label htmlFor="recipientAddress" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="recipientAddress"
                  type="text"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientAddress', { required: 'Recipient address is required' })}
                  className={`input ${errors.recipientAddress ? 'border-red-500' : ''}`}
                  placeholder="456 Oak Avenue"
                />
                {errors.recipientAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientAddress.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="recipientCity" className="block text-sm font-medium text-gray-700 mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="recipientCity"
                  type="text"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientCity', { required: 'Recipient city is required' })}
                  className={`input ${errors.recipientCity ? 'border-red-500' : ''}`}
                  placeholder="Los Angeles"
                />
                {errors.recipientCity && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientCity.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="recipientState" className="block text-sm font-medium text-gray-700 mb-1.5">
                  State/Province
                </label>
                <input
                  id="recipientState"
                  type="text"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientState')}
                  className="input"
                  placeholder="CA"
                />
              </div>

              <div>
                <label htmlFor="recipientPostalCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="recipientPostalCode"
                  type="text"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientPostalCode', { required: 'Postal code is required' })}
                  className={`input ${errors.recipientPostalCode ? 'border-red-500' : ''}`}
                  placeholder="90210"
                />
                {errors.recipientPostalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientPostalCode.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="recipientCountry" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  id="recipientCountry"
                  onFocus={() => setActiveStep(1)}
                  {...register('recipientCountry', { required: 'Country is required' })}
                  className={`input ${errors.recipientCountry ? 'border-red-500' : ''}`}
                >
                  <option value="">Select country...</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.recipientCountry && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientCountry.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Package Information */}
          <div className="card" ref={packageRef}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-[#0B1F3A] flex items-center justify-center">
                <CubeIcon className="h-6 w-6 text-[#FF6B00]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0B1F3A]">Package Information</h2>
                <p className="text-xs text-[#64748B]">Package details and shipping options</p>
              </div>
            </div>

            {/* Service Type Cards */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Service Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {serviceTypes.map(service => {
                  const ServiceIcon = service.icon;
                  const isSelected = watchedServiceType === service.value;
                  return (
                    <button
                      key={service.value}
                      type="button"
                      onClick={() => handleServiceTypeChange(service.value)}
                      onFocus={() => setActiveStep(2)}
                      className={`relative border-2 rounded-xl p-4 text-left transition-all ${
                        isSelected
                          ? 'border-[#FF6B00] bg-orange-50 ring-2 ring-orange-100'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <CheckCircleIcon className="h-5 w-5 text-[#FF6B00]" />
                        </div>
                      )}
                      <ServiceIcon className={`h-7 w-7 mb-2 ${isSelected ? 'text-[#FF6B00]' : 'text-gray-400'}`} />
                      <div className="font-semibold text-sm text-[#0B1F3A]">{service.label}</div>
                      <div className="text-xs text-[#64748B] mt-0.5">{service.days}</div>
                      <div className="text-sm font-bold text-[#0B1F3A] mt-1">{service.price}</div>
                    </button>
                  );
                })}
              </div>
              <input type="hidden" {...register('serviceType', { required: 'Service type is required' })} />
              {errors.serviceType && (
                <p className="mt-1 text-sm text-red-600">{errors.serviceType.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label htmlFor="packageDescription" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Package Description
                </label>
                <input
                  id="packageDescription"
                  type="text"
                  onFocus={() => setActiveStep(2)}
                  {...register('packageDescription')}
                  className="input"
                  placeholder="Electronics, Documents, Clothing, etc."
                />
              </div>

              <div>
                <label htmlFor="packageWeight" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Weight (kg)
                </label>
                <input
                  id="packageWeight"
                  type="number"
                  step="0.1"
                  min="0"
                  onFocus={() => setActiveStep(2)}
                  {...register('packageWeight', { min: 0 })}
                  className="input"
                  placeholder="2.5"
                />
              </div>

              <div>
                <label htmlFor="packageDimensions" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Dimensions (L x W x H cm)
                </label>
                <input
                  id="packageDimensions"
                  type="text"
                  onFocus={() => setActiveStep(2)}
                  {...register('packageDimensions')}
                  className="input"
                  placeholder="30 x 20 x 15"
                />
              </div>

              <div>
                <label htmlFor="packageValue" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Declared Value ($)
                </label>
                <input
                  id="packageValue"
                  type="number"
                  step="0.01"
                  min="0"
                  onFocus={() => setActiveStep(2)}
                  {...register('packageValue', { min: 0 })}
                  className="input"
                  placeholder="100.00"
                />
              </div>

              <div>
                <label htmlFor="progressPercentage" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Initial Progress (0-100%)
                </label>
                <input
                  id="progressPercentage"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  onFocus={() => setActiveStep(2)}
                  {...register('progressPercentage', { min: 0, max: 100, valueAsNumber: true })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF6B00]"
                />
                <p className="mt-1 text-xs text-gray-500">0% = source, 100% = destination</p>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="estimatedDelivery" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Estimated Delivery Date
                </label>
                <input
                  id="estimatedDelivery"
                  type="date"
                  onFocus={() => setActiveStep(2)}
                  {...register('estimatedDelivery')}
                  className="input"
                />
                <p className="mt-1 text-xs text-gray-500">Auto-calculated from service type — adjust if needed</p>
              </div>
            </div>

            {/* Special Options */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                watch('signatureRequired') ? 'border-[#FF6B00] bg-orange-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  id="signatureRequired"
                  type="checkbox"
                  {...register('signatureRequired')}
                  className="h-5 w-5 text-[#FF6B00] focus:ring-orange-500 border-gray-300 rounded"
                />
                <div>
                  <div className="text-sm font-medium text-gray-700">Signature Required</div>
                  <div className="text-xs text-gray-500">Recipient must sign on delivery</div>
                </div>
              </label>
              
              <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                watch('fragile') ? 'border-[#FF6B00] bg-orange-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  id="fragile"
                  type="checkbox"
                  {...register('fragile')}
                  className="h-5 w-5 text-[#FF6B00] focus:ring-orange-500 border-gray-300 rounded"
                />
                <div>
                  <div className="text-sm font-medium text-gray-700">Fragile Package</div>
                  <div className="text-xs text-gray-500">Handle with extra care</div>
                </div>
              </label>
            </div>

            {/* Special Instructions */}
            <div className="mt-6">
              <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700 mb-1.5">
                Special Instructions
              </label>
              <textarea
                id="specialInstructions"
                rows={3}
                onFocus={() => setActiveStep(2)}
                {...register('specialInstructions')}
                className="input"
                placeholder="Any special handling instructions for the courier..."
              />
            </div>
          </div>
        </form>
      </div>

      {/* Sticky Submit Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/shipments')}
            className="text-sm font-medium text-[#64748B] hover:text-[#0B1F3A] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn btn-primary flex items-center gap-2 px-6 py-2.5"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
            <span>{isSubmitting ? 'Creating...' : 'Create Shipment'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateShipment;
